import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import CopyPasteTable from '../../organisms/CopyPasteTable';
import TextEdit from '../../molecules/TextEdit';
import NumberEdit from '../../molecules/NumberEdit';
import CurrencyInput from '../../molecules/CurrencyInput';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { fundingApi, CreateInstrumentItem, InstrumentProduct, Platform, RateType } from '../../../services/fundingApi';
import { extractAndConvertFrom } from '../../../utils/DateUtils';
import {
    BROKER_FIELD_MAPPING,
    ISSUANCE_HEADERS,
    PastedRow,
    normalizePlatformName,
    parseNikosBlock,
    toCreateItem,
    todayIso,
} from './issuanceMapping';

interface Form {
    platformId: string;
    issuances?: PastedRow[];
}

const VALID_PRODUCTS: InstrumentProduct[] = ['LCA', 'CDB', 'DI'];
const VALID_RATE_TYPES: RateType[] = ['PRE', 'POS'];

// DI não tem PU Desagiado (liquida direto com a contraparte via CETIP) e
// pode ter um spread aditivo sobre o indexador (ex: "100% CDI + 1,85% Pré") -
// ver docs/context/business-context.md secao 8.2.
const requiresPuDegassed = (product: InstrumentProduct | ''): boolean => product !== 'DI';

const PRODUCT_SELECT_OPTIONS = VALID_PRODUCTS.map((p) => ({ value: p, label: p }));
const RATE_TYPE_SELECT_OPTIONS = VALID_RATE_TYPES.map((r) => ({ value: r, label: r }));

// Validações de um único item, sem prefixo de linha - usado tanto pelo modo
// de colagem (que prefixa "Linha N:") quanto pelo cadastro manual (item único).
const itemProblems = (item: CreateInstrumentItem): string[] => {
    const problems: string[] = [];

    if (!VALID_PRODUCTS.includes(item.product)) {
        problems.push(`produto "${item.product || '(vazio)'}" inválido - aceito apenas LCA, CDB ou DI.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.issue_date)) {
        problems.push(`data da aplicação "${item.issue_date || '(vazio)'}" fora do formato esperado.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.due_date)) {
        problems.push(`vencimento "${item.due_date || '(vazio)'}" fora do formato esperado (DD/MM/AAAA).`);
    }
    if (!VALID_RATE_TYPES.includes(item.rate_type)) {
        problems.push('indexador (PRE/POS) inválido.');
    }
    if (item.rate_type === 'POS' && !item.remuneration_index) {
        problems.push('índice não informado (obrigatório quando o indexador é POS).');
    }
    if (!item.quantity || item.quantity <= 0) {
        problems.push('quantidade inválida.');
    }
    if (!item.tax || item.tax <= 0) {
        problems.push('taxa inválida.');
    }
    if (!item.pu || item.pu <= 0) {
        problems.push('PU inválido.');
    }
    if (requiresPuDegassed(item.product) && (!item.pu_degassed || item.pu_degassed <= 0)) {
        problems.push('PU desagiado inválido.');
    }
    if (!item.financial_value || item.financial_value <= 0) {
        problems.push('valor inválido.');
    }

    return problems;
};

const validateItems = (items: CreateInstrumentItem[], platformId: string): string[] => {
    const problems: string[] = [];

    if (!platformId) {
        problems.push('Selecione a corretora/plataforma.');
    }

    items.forEach((item, idx) => {
        itemProblems(item).forEach((p) => problems.push(`Linha ${idx + 1}: ${p}`));
    });

    return problems;
};

type ManualEntry = {
    product: InstrumentProduct | '';
    issueDate: string;
    dueDate: string;
    rateType: RateType;
    remunerationIndex: string;
    tax: number;
    quantity: number;
    pu: number;
    puDegassed: number;
    financialValue: number;
    diFuturo: number;
    additionalSpread: number;
};

const EMPTY_MANUAL_ENTRY: ManualEntry = {
    product: '',
    issueDate: '',
    dueDate: '',
    rateType: 'PRE',
    remunerationIndex: '',
    tax: 0,
    quantity: 0,
    pu: 0,
    puDegassed: 0,
    financialValue: 0,
    diFuturo: 0,
    additionalSpread: 0,
};

const IssuanceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const form = useSignal<Form>({ platformId: '', issuances: [] });
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const hasErrors = useSignal(false);
    const currentMapping = useSignal<Record<string, string>>({});

    const platforms = useSignal<Platform[]>([]);
    const isLoadingPlatforms = useSignal(false);

    const entryMode = useSignal<'paste' | 'manual'>('paste');
    const manualEntry = useSignal<ManualEntry>(EMPTY_MANUAL_ENTRY);
    const manualItems = useSignal<CreateInstrumentItem[]>([]);

    useEffect(() => {
        const loadPlatforms = async () => {
            isLoadingPlatforms.value = true;
            const result = await fundingApi.listPlatforms();
            isLoadingPlatforms.value = false;

            if (!result.ok) {
                toast.error(`Não foi possível carregar as corretoras: ${result.message}`);
                return;
            }

            platforms.value = result.data.filter((p) => p.active);
        };
        loadPlatforms();
    }, []);

    useEffect(() => {
        const handleJson = (event: CustomEvent) => {
            const data = event.detail as PastedRow[];
            form.value = { ...form.value, issuances: data };
            buttonDisabled.value = hasErrors.value || data.length === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
        return () => window.removeEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
    }, []);

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            const errors = event.detail as unknown[];
            hasErrors.value = errors.length > 0;
            buttonDisabled.value = hasErrors.value || (form.value.issuances?.length || 0) === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:errors', handleErrors as EventListener);
        return () => window.removeEventListener('CopyPasteTable:errors', handleErrors as EventListener);
    }, [form.value.issuances?.length]);

    const clearData = () => {
        window.dispatchEvent(new Event('CopyPasteTable:clearData'));
    };

    const selectPlatform = (value: string) => {
        const platform = platforms.value.find((p) => p.id === value);
        if (!platform) return;

        form.value = { ...form.value, platformId: value };
        currentMapping.value = BROKER_FIELD_MAPPING[normalizePlatformName(platform.name)] || {};
        clearData();
        manualItems.value = [];
    };

    // Trocar de modo descarta os dados do modo anterior - evita o cenário de
    // colar 5 linhas, trocar pra manual, adicionar 1 e enviar só essa 1 sem
    // o usuário perceber que as outras 5 ficaram de fora do envio.
    const switchMode = (mode: 'paste' | 'manual') => {
        if (mode === entryMode.value) return;
        entryMode.value = mode;
        clearData();
        manualItems.value = [];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const addManualItem = () => {
        if (!form.value.platformId) {
            toast.error('Selecione a corretora/plataforma antes de adicionar.');
            return;
        }

        const item: CreateInstrumentItem = {
            product: manualEntry.value.product as InstrumentProduct,
            issue_date: manualEntry.value.issueDate || todayIso(),
            due_date: manualEntry.value.dueDate,
            rate_type: manualEntry.value.rateType,
            remuneration_index: manualEntry.value.rateType === 'POS' ? manualEntry.value.remunerationIndex.trim().toUpperCase() : null,
            tax: manualEntry.value.tax,
            quantity: Math.round(manualEntry.value.quantity),
            pu: manualEntry.value.pu,
            pu_degassed: requiresPuDegassed(manualEntry.value.product) ? manualEntry.value.puDegassed : null,
            financial_value: manualEntry.value.financialValue,
            di_futuro: manualEntry.value.rateType === 'POS' && manualEntry.value.diFuturo ? manualEntry.value.diFuturo : null,
            additional_spread: manualEntry.value.product === 'DI' && manualEntry.value.additionalSpread ? manualEntry.value.additionalSpread : null,
            platform_id: form.value.platformId,
        };

        const problems = itemProblems(item);
        if (problems.length > 0) {
            problems.forEach((p) => toast.error(p));
            return;
        }

        manualItems.value = [...manualItems.value, item];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const removeManualItem = (idx: number) => {
        manualItems.value = manualItems.value.filter((_, i) => i !== idx);
    };

    const isManualMode = () => entryMode.value === 'manual';

    const saveDisabled = isManualMode()
        ? (isSending.value || !form.value.platformId || manualItems.value.length === 0)
        : (isSending.value || buttonDisabled.value);

    const submitForm = async () => {
        if (saveDisabled) {
            toast.error('Por favor, corrija os erros antes de enviar o formulário.');
            return;
        }

        if (!user?.email) {
            toast.error('Não foi possível identificar o usuário logado.');
            return;
        }

        const platformId = form.value.platformId;
        const platformName = platforms.value.find((p) => p.id === platformId)?.name || '';
        const items = isManualMode()
            ? manualItems.value
            : (form.value.issuances || []).map((row) => toCreateItem(row, platformId, platformName));
        const problems = validateItems(items, platformId);
        if (problems.length > 0) {
            problems.slice(0, 5).forEach((message) => toast.error(message));
            return;
        }

        isSending.value = true;
        const result = await fundingApi.createInstruments(items, user.username);
        isSending.value = false;

        if (!result.ok) {
            if (result.errors.length > 0) {
                result.errors.slice(0, 5).forEach((err) => toast.error(`${err.fieldName}: ${err.message}`));
            } else {
                toast.error(result.message);
            }
            return;
        }

        toast.info(`${result.data.createdIds.length} emissão(ões) lançada(s) com sucesso.`);
        navigate('/funding/issuance/list');
    };

    const cancelForm = () => navigate(-1);

    // Conta CETIP é por produto desde 2026-08-19 (Daycoval usa contas diferentes
    // para LCA e CDB) - mostra as duas só quando divergem, senão só uma. DI
    // (ex: SICOOB, ITAU UNIBANCO) só tem cetipAccountDi - LCA/CDB ficam null.
    const formatCetipLabel = (p: Platform): string => {
        if (p.cetipAccountLca && p.cetipAccountCdb && p.cetipAccountLca !== p.cetipAccountCdb) {
            return `CETIP LCA ${p.cetipAccountLca} / CDB ${p.cetipAccountCdb}`;
        }
        const account = p.cetipAccountLca || p.cetipAccountCdb || p.cetipAccountDi;
        return account ? `CETIP ${account}` : 'sem conta CETIP cadastrada';
    };

    const platformOptions = platforms.value.map((p) => ({
        value: p.id,
        label: `${p.name} — ${formatCetipLabel(p)}`,
    }));

    const hasKnownMapping = Object.keys(currentMapping.value).length > 0;
    const selectedPlatform = platforms.value.find((p) => p.id === form.value.platformId);
    // Nikos não manda planilha tabular (cabeçalho+linhas) - cada colagem é o
    // bloco inteiro de 1 operação, que vira 1 linha (ver parseNikosBlock em
    // issuanceMapping.ts). Não usa columnMapping/hasKnownMapping.
    const isNikos = selectedPlatform ? normalizePlatformName(selectedPlatform.name) === 'NIKOS' : false;

    return (
        <PageForm
            pageTitle='Lançar Emissões'
            apiVersion='Funding API'
            isLoading={false}
            breadcrumb={[{ label: 'Tesouraria', url: '/funding/issuance/list' }, { label: 'Lançar Emissões', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {!isManualMode() && form.value.issuances && form.value.issuances.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={clearData}
                            disabled={isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    {isManualMode() && manualItems.value.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={() => { manualItems.value = []; }}
                            disabled={isSending.value}
                        >
                            Limpar Itens
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700'
                        onClick={submitForm}
                        disabled={saveDisabled}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>
                <SelectFilter
                    id='slc-broker'
                    label='Corretora:'
                    value={form.value.platformId}
                    onChange={selectPlatform}
                    defaultVoidOption
                    defaultLabel={isLoadingPlatforms.value ? 'Carregando corretoras...' : 'Selecione a corretora...'}
                    options={platformOptions}
                />

                {form.value.platformId && (
                    <div className='flex gap-2 border-b border-gray-200 dark:border-gray-700'>
                        <button
                            type='button'
                            onClick={() => switchMode('paste')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'paste'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Colar planilha
                        </button>
                        <button
                            type='button'
                            onClick={() => switchMode('manual')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'manual'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Cadastro manual
                        </button>
                    </div>
                )}

                {form.value.platformId && entryMode.value === 'paste' && !hasKnownMapping && !isNikos && (
                    <p className='text-sm text-amber-600 dark:text-amber-400'>
                        Não há mapeamento automático de colunas conhecido para esta corretora ainda -
                        cole os dados e arraste as colunas na ordem correta (Produto, Data Aplicação, Vencimento,
                        Indexador, Taxa, Quantidade, PU, PU Desagiado, Valor, DI Futuro).
                    </p>
                )}

                {form.value.platformId && entryMode.value === 'paste' && isNikos && (
                    <p className='text-sm text-amber-600 dark:text-amber-400'>
                        A Nikos manda uma planilha por operação (não uma tabela) - copie a área de dados
                        inteira do Excel dela (Papel, Valor, P.U, Quantidade, Emissão, Vencimento, Dias Uteis,
                        Taxa Cliente/Tx Nikos) e cole abaixo. Cada colagem vira uma linha nova; repita para
                        cada operação e salve ao final. O PU Desagiado não vem calculado na planilha da Nikos -
                        é calculado aqui com as taxas informadas, usando a mesma fórmula já validada no backend.
                    </p>
                )}

                {form.value.platformId && entryMode.value === 'paste' && (
                    <CopyPasteTable
                        fixedHeaders={ISSUANCE_HEADERS}
                        allowDuplicates={true}
                        // Sem mapeamento conhecido (ex: GENIAL INST), não passar
                        // columnMapping - um objeto vazio faria o CopyPasteTable tratar a
                        // 1ª linha colada como cabeçalho e zerar todos os campos (nenhuma
                        // coluna bateria). undefined cai no fallback manual (drag-and-drop).
                        columnMapping={hasKnownMapping ? currentMapping.value : undefined}
                        // Nikos: cada colagem é 1 bloco/operação que vira 1 linha nova,
                        // acumulando (não substituindo) - ver appendMode/parseBlockPaste
                        // em CopyPasteTable.tsx.
                        appendMode={isNikos}
                        parseBlockPaste={isNikos ? parseNikosBlock : undefined}
                        onBlockParseError={isNikos ? () => toast.error(
                            'Bloco colado não reconhecido - confira se copiou a área de dados completa da planilha da Nikos (Papel, Valor, P.U, Quantidade, Emissão, Vencimento, Dias Uteis, Taxa Cliente/Tx Nikos).',
                        ) : undefined}
                    />
                )}

                {form.value.platformId && entryMode.value === 'manual' && (
                    <div className='space-y-4'>
                        <div className='grid grid-cols-3 gap-4'>
                            <SelectFilter
                                id='manual-product'
                                label='Produto'
                                value={manualEntry.value.product}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, product: v as InstrumentProduct }; }}
                                defaultVoidOption
                                defaultLabel='Selecione...'
                                options={PRODUCT_SELECT_OPTIONS}
                            />
                            <TextEdit
                                id='manual-issue-date'
                                label='Data da Aplicação'
                                type='date'
                                value={manualEntry.value.issueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, issueDate: e.target.value }; }}
                                placeholder={todayIso()}
                            />
                            <TextEdit
                                id='manual-due-date'
                                label='Vencimento'
                                type='date'
                                value={manualEntry.value.dueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, dueDate: e.target.value }; }}
                            />
                            <SelectFilter
                                id='manual-rate-type'
                                label='Indexador'
                                value={manualEntry.value.rateType}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, rateType: v as RateType }; }}
                                options={RATE_TYPE_SELECT_OPTIONS}
                            />
                            {manualEntry.value.rateType === 'POS' && (
                                <TextEdit
                                    id='manual-index'
                                    label='Índice (ex: CDI)'
                                    value={manualEntry.value.remunerationIndex}
                                    onChange={(e) => { manualEntry.value = { ...manualEntry.value, remunerationIndex: e.target.value }; }}
                                />
                            )}
                            <CurrencyInput
                                id='manual-tax'
                                label='Taxa (%)'
                                value={manualEntry.value.tax}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, tax: v }; }}
                                fractionDigits={2}
                            />
                            <NumberEdit
                                id='manual-quantity'
                                label='Quantidade'
                                value={manualEntry.value.quantity || ''}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, quantity: parseInt(e.target.value || '0', 10) }; }}
                            />
                            <CurrencyInput
                                id='manual-pu'
                                label='PU (R$)'
                                value={manualEntry.value.pu}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, pu: v }; }}
                                fractionDigits={2}
                            />
                            {requiresPuDegassed(manualEntry.value.product) && (
                                <CurrencyInput
                                    id='manual-pu-degassed'
                                    label='PU Desagiado (R$)'
                                    value={manualEntry.value.puDegassed}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, puDegassed: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                            <CurrencyInput
                                id='manual-financial-value'
                                label='Valor (R$)'
                                value={manualEntry.value.financialValue}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, financialValue: v }; }}
                                fractionDigits={2}
                            />
                            {manualEntry.value.rateType === 'POS' && (
                                <CurrencyInput
                                    id='manual-di-futuro'
                                    label='DI Futuro (%) - opcional'
                                    value={manualEntry.value.diFuturo}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, diFuturo: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                            {manualEntry.value.product === 'DI' && (
                                <CurrencyInput
                                    id='manual-additional-spread'
                                    label='Spread Adicional (%) - opcional'
                                    value={manualEntry.value.additionalSpread}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, additionalSpread: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                        </div>
                        <div>
                            <button
                                type='button'
                                onClick={addManualItem}
                                className='px-4 py-2 text-sm border border-gray-600 text-gray-600 rounded hover:bg-gray-100 dark:border-gray-400 dark:text-gray-400 dark:hover:bg-slate-700'
                            >
                                Adicionar emissão
                            </button>
                        </div>

                        {manualItems.value.length > 0 && (
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700'>
                                        <th className='pb-2'>Produto</th>
                                        <th className='pb-2'>Aplicação</th>
                                        <th className='pb-2'>Vencimento</th>
                                        <th className='pb-2'>Indexador</th>
                                        <th className='pb-2'>Taxa</th>
                                        <th className='pb-2 text-right'>Quantidade</th>
                                        <th className='pb-2 text-right'>PU</th>
                                        <th className='pb-2 text-right'>PU Desagiado</th>
                                        <th className='pb-2 text-right'>Valor</th>
                                        <th className='pb-2'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manualItems.value.map((item, idx) => (
                                        <tr key={idx} className='border-b border-gray-100 dark:border-slate-700'>
                                            <td className='py-2'>{item.product}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.issue_date)}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.due_date)}</td>
                                            <td className='py-2'>{item.rate_type === 'POS' ? `${item.remuneration_index} (POS)` : 'PRE'}</td>
                                            <td className='py-2'>{item.tax.toFixed(2)}%</td>
                                            <td className='py-2 text-right'>{item.quantity}</td>
                                            <td className='py-2 text-right'>{item.pu.toFixed(2)}</td>
                                            <td className='py-2 text-right'>{item.pu_degassed !== null ? item.pu_degassed.toFixed(2) : '-'}</td>
                                            <td className='py-2 text-right'>{item.financial_value.toFixed(2)}</td>
                                            <td className='py-2 text-right'>
                                                <button
                                                    type='button'
                                                    onClick={() => removeManualItem(idx)}
                                                    className='text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400'
                                                    title='Remover'
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </PageForm>
    );
};

export default IssuanceFormPage;
